import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "../Css/figmaMeasureTag.css";

const MOBILE_QUERY = "(max-width: 768px)";

// Which raw pointer-delta sign grows the box for each dragged handle —
// e.g. dragging the bottom-right handle right/down grows it, dragging the
// top-left handle left/up grows it. The text itself never scales — only
// the box grows/shrinks around it, staying centered via flex.
const HANDLE_CONFIG = {
  tl: { dx: -1, dy: -1 },
  tr: { dx: 1, dy: -1 },
  bl: { dx: -1, dy: 1 },
  br: { dx: 1, dy: 1 },
};

// Wraps inline content with a Figma-inspect-style selection outline —
// draggable corner handles + a floating "W × H" dimension tag that tracks
// the live (possibly manually resized) box size. The wrapped text always
// stays its normal size, centered in the box — dragging a handle only
// grows/shrinks the box around it.
//
// `position`/`mobilePosition` pick which side of the box the tag sits on,
// independently per breakpoint — some headings wrap tightly enough that
// the safe side flips between desktop and mobile (mobilePosition defaults
// to `position` if not given). `hideOnMobile` fully hides the annotation
// below 768px for spots with no safe side at all.
//
// Once resized, a "Reset" control snaps it back to its natural size — by
// default that's a small button right next to the box, but a caller that
// wants it to live elsewhere in the page (e.g. next to a nearby badge) can
// pass `showInlineReset={false}` and instead drive it externally via
// `onResizeChange(isResized)` + an imperative `ref.current.reset()`.
//
// `showDimensions` hides just the floating "W × H" tag (handles + box stay)
// for spots where the annotation is wanted but the number isn't.
const FigmaMeasureTag = forwardRef(function FigmaMeasureTag(
  {
    children,
    label,
    position = "top",
    mobilePosition,
    hideOnMobile = false,
    showInlineReset = true,
    showDimensions = true,
    onResizeChange,
  },
  ref,
) {
  const wrapRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [drag, setDrag] = useState(null);
  const [resized, setResized] = useState(null); // { width, height } once manually dragged

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Ignore resizes caused by our own manual override so it doesn't
      // fight with a drag in progress or overwrite a resized state.
      setNaturalSize((prev) => {
        if (drag || resized) return prev;
        return { width: Math.round(width), height: Math.round(height) };
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onResizeChange) onResizeChange(Boolean(resized));
  }, [resized, onResizeChange]);

  const resetSize = useCallback(() => setResized(null), []);
  useImperativeHandle(ref, () => ({ reset: resetSize }), [resetSize]);

  const onHandlePointerDown = useCallback((handle) => (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    const rect = wrapRef.current.getBoundingClientRect();
    try {
      evt.target.setPointerCapture(evt.pointerId);
    } catch (err) {
      // Rare environments without capture support still work via the
      // window-level listeners below, just without pointer capture.
    }
    setDrag({
      handle,
      startX: evt.clientX,
      startY: evt.clientY,
      startW: rect.width,
      startH: rect.height,
    });
  }, []);

  useEffect(() => {
    if (!drag) return;

    const onMove = (evt) => {
      const cfg = HANDLE_CONFIG[drag.handle];
      const deltaX = (evt.clientX - drag.startX) * cfg.dx;
      const deltaY = (evt.clientY - drag.startY) * cfg.dy;
      const newW = Math.max(30, Math.min(drag.startW * 3, drag.startW + deltaX));
      const newH = Math.max(20, Math.min(drag.startH * 3, drag.startH + deltaY));
      setResized({ width: newW, height: newH });
    };
    const onUp = () => setDrag(null);

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [drag]);

  const displaySize = resized
    ? { width: Math.round(resized.width), height: Math.round(resized.height) }
    : naturalSize;
  const effectivePosition = isMobile ? mobilePosition || position : position;

  const wrapStyle = resized
    ? { width: `${resized.width}px`, height: `${resized.height}px` }
    : undefined;

  return (
    <span
      className={[
        "figma-tag-wrap",
        hideOnMobile ? "figma-tag-hide-mobile" : "",
        drag ? "dragging" : "",
        resized ? "resized" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      ref={wrapRef}
      style={wrapStyle}
    >
      <span className="figma-tag-content">{children}</span>
      <span className="figma-tag-box" aria-hidden="true">
        {["tl", "tr", "bl", "br"].map((handle) => (
          <span
            key={handle}
            className={`figma-tag-handle ${handle}`}
            onPointerDown={onHandlePointerDown(handle)}
          />
        ))}
      </span>
      {showDimensions && displaySize && (
        <span
          className={`figma-tag-dimensions pos-${effectivePosition}`}
          aria-hidden="true"
        >
          {label ? `${label} · ` : ""}
          {displaySize.width} × {displaySize.height}
        </span>
      )}
      {resized && showInlineReset && (
        <button
          type="button"
          className="figma-tag-reset"
          onClick={resetSize}
          onPointerDown={(evt) => evt.stopPropagation()}
        >
          Reset
        </button>
      )}
    </span>
  );
});

export default FigmaMeasureTag;
